"use client"

import styled from "@emotion/styled"
import { Box, Breadcrumbs, Container } from "@mui/material"
import Link from "next/link"
import React from "react"
import { iconsMap } from "../../public/icons/iconsMap"
import { colors, mediaQueries, sizes } from "../theme/theme"

/**
 * Breadcrumbs schema for CustomBreadcrumbs component
 *
 * @typedef {Object} BreadcrumbItem
 * @property {string} title - The display title of the breadcrumb
 * @property {string} url - The URL for the breadcrumb link
 *
 * @typedef {Object} BreadcrumbsSchema
 * @property {BreadcrumbItem} selfObject - The current page breadcrumb
 * @property {BreadcrumbItem[]} parents - The parent breadcrumbs in order from root to current
 */

/**
 * Example breadcrumbs schema:
 *
 * const breadcrumbs = {
 *   selfObject: { title: "Current Page", url: "/current" },
 *   parents: [
 *     { title: "Home", url: "/" },
 *     { title: "Section", url: "/section" },
 *   ]
 * }
 */

interface CustomBreadcrumbsProps {
  breadcrumbs: {
    selfObject: { title: string; url: string }
    parents: { title: string; url: string }[]
  }
  textColor?: string
  className?: string
  zIndex?: number
  loadSchema?: boolean
}

const StyledLimitWidth = styled(Box)<{ zIndex?: number }>`
  position: absolute;
  display: flex;
  flex-direction: row;
  width: fit-content;
  top: 110px;
  padding: ${sizes.baseDouble} 0;
  z-index: ${(props) => props.zIndex || 3};
  max-width: ${sizes.maxWidthContainer};
  ${mediaQueries.mobile} {
    top: 95px;
    left: ${sizes.baseTriple};
    padding: 0;
  }
  ${mediaQueries.desktopM} {
    width: 100%;
    padding: ${sizes.baseDouble};
  }
  ${mediaQueries.desktopXl} {
    width: 100%;
    padding: ${sizes.baseDouble} 0;
  }
`

const BreadcrumbsWrapper = styled(Container, {
  shouldForwardProp: (prop) => prop !== "textColor",
})<{ textColor: string }>`
  display: flex;
  max-width: ${sizes.maxWidthContainer};
  ${mediaQueries.desktopXl} {
    max-width: ${sizes.maxWidthContainerXl};
  }
  padding: 0;
  li {
    padding: 0;
    &:last-child {
      font-weight: bold;
    }
    a {
      color: ${(props) => props.textColor};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
    span {
      color: ${(props) => props.textColor};
      text-decoration: none;
    }

    svg {
      path {
        stroke: ${(props) => props.textColor};
      }
    }
  }
  svg {
    margin: 0 ${sizes.baseSingle};
  }
`

// Get origin from environment variable
const ORIGIN = process.env.ORIGIN || ""

export const CustomBreadcrumbs: React.FC<CustomBreadcrumbsProps> = ({
  breadcrumbs,
  textColor = colors.black,
  className,
  zIndex,
  loadSchema = true,
}) => {
  const ArrowIcon = iconsMap.left_arrow
  const { selfObject, parents } = breadcrumbs

  // Helper to make URLs absolute
  const makeAbsoluteUrl = (url: string) => {
    if (!url) return ""
    if (url.startsWith("http")) return url
    // Ensure no double slashes
    return ORIGIN.replace(/\/$/, "") + (url.startsWith("/") ? url : `/${url}`)
  }

  // Build BreadcrumbList schema.org structured data with absolute URLs
  const breadcrumbList = [
    ...(parents || []).map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.title,
      item: makeAbsoluteUrl(item.url),
    })),
    selfObject
      ? {
          "@type": "ListItem",
          position: (parents?.length || 0) + 1,
          name: selfObject.title,
          item: makeAbsoluteUrl(selfObject.url),
        }
      : null,
  ].filter(Boolean)

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbList,
  }

  return (
    <>
      {loadSchema && (
        <script
          id="breadcrumb-schema"
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }}
          // strategy="beforeInteractive"
        />
      )}

      <StyledLimitWidth className={className} zIndex={zIndex}>
        <BreadcrumbsWrapper textColor={textColor}>
          <Breadcrumbs role="navigation" separator={<ArrowIcon />}>
            {parents &&
              parents.map((item, index) => {
                if (!item) {
                  return null
                }
                const { title, url } = item
                return (
                  <Link key={`crumb-${title}-${index}`} href={url}>
                    {item.title}
                  </Link>
                )
              })}
            {selfObject && <span>{selfObject.title}</span>}
          </Breadcrumbs>
        </BreadcrumbsWrapper>
      </StyledLimitWidth>
    </>
  )
}
export default CustomBreadcrumbs
